import { useState, useEffect } from "react"
import { useUpdateTicketMutation, useDeleteTicketMutation } from "./ticketsApiSlice"
import { useNavigate } from "react-router-dom"
import useAuth from "../../hooks/useAuth"
import useLang from '../../hooks/useLang'
import useTranslate from '../../hooks/useTranslate'

// import `ChakraProvider` component
import { Box, Grid, GridItem, Center, HStack, Container, Input, Switch, Button, IconButton, Text, useToast } from '@chakra-ui/react'
import { FormControl, FormLabel } from "@chakra-ui/react"

// import `ChakraProvider` icon
import { ArrowBackIcon, CheckIcon, CloseIcon, AddIcon, MinusIcon } from '@chakra-ui/icons'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTicket } from "@fortawesome/free-solid-svg-icons"

const EditTicketForm = ({ Ticket, countries }) => {

    const { isManager, isAdmin } = useAuth()

    const { lang } = useLang()
    const { T } = useTranslate(lang)

    const [updateTicket, {
        data,
        isLoading,
        isSuccess,
        isError,
        error
    }] = useUpdateTicketMutation()

    const [deleteTicket, {
        data: delData,
        isSuccess: isDelSuccess,
        isError: isDelError,
        error: delError
    }] = useDeleteTicketMutation()

    const navigate = useNavigate()

    let [canSave, setCanSave] = useState(false)

    const [ticketStatus, setTicketStatus] = useState(Ticket.ticketStatus)

    const toastInfo = useToast({
        position: 'bottom-left',
        variant: 'left-accent',
    })

    const checkCanSave = () => {
        setCanSave(true)
        formFieldsTicketName.map((form) => {
            if (!isLoading && (form.lang === '' || form.ticketName === '')) {
                setCanSave(false)
            }
        })
    }

    const onPageBack = () => navigate('/dash/tickets')

    const onTicketStatusChanged = e => setTicketStatus(prev => !prev)

    const onSaveTicketClicked = async (e) => {
        e.preventDefault()
        if (canSave) {
            let output = []
            let ticketNameByLang = {}
            formFieldsTicketName.forEach(field => {
                ticketNameByLang[field.lang] = field.ticketName
            })
            output['ticketName'] = ticketNameByLang
            console.log(output)

            await updateTicket({ id: Ticket.id, ticketName: ticketNameByLang, ticketStatus })
        }
    }

    const onDeleteTicketClicked = async () => {
        await deleteTicket({ id: Ticket.id })
    }

    const created = new Date(Ticket.createdAt).toLocaleString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })
    const updated = new Date(Ticket.updatedAt).toLocaleString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' })

    let deleteButton = null
    if (isManager || isAdmin) {
        deleteButton = (
            <Button size='xs' onClick={onDeleteTicketClicked} name="Delete" title={T.common.Delete} leftIcon={<CloseIcon />} colorScheme='red' variant='outline'>{T.tickets.DeleteThisTicket}</Button>
        )
    }

    const initTicketNamesForm = () => {
        let ticketNameByLang = []
        Object.entries(Ticket.ticketName).map(item => {
            ticketNameByLang.push({ lang: item[0], ticketName: item[1] })
        })
        return ticketNameByLang
    }

    const [formFieldsTicketName, setFormFields] = useState(initTicketNamesForm)

    const handleFormChange = (event, index) => {
        let data = [...formFieldsTicketName];
        data[index][event.target.name] = event.target.value;
        setFormFields(data);
        checkCanSave()
    }

    const addFields = () => {
        let object = {
            lang: '',
            ticketName: ''
        }
        setFormFields([...formFieldsTicketName, object])
        setCanSave(false)
    }

    const removeFields = (index) => {
        let data = [...formFieldsTicketName];
        data.splice(index, 1)
        setFormFields(data)
        checkCanSave()
    }

    useEffect(() => {
        checkCanSave()
        if (isSuccess) {
            toastInfo({ title: 'TICKET : ' + data?.message + ' successfully', status: 'success' })
            initTicketNamesForm()
            navigate('/dash/tickets')
        }
        if (isError) {
            toastInfo({ title: 'TICKET : Update error (' + error?.data?.message + ')', status: 'error' })
        }
        if (isDelSuccess) {
            toastInfo({ title: 'TICKET : ' + delData?.message + ' successfully', status: 'success' })
            initTicketNamesForm()
            navigate('/dash/tickets')
        }
        if (isDelError) {
            toastInfo({ title: 'TICKET : Update error (' + delError?.data?.message + ')', status: 'error' })
        }
    }, [checkCanSave, initTicketNamesForm, toastInfo, isError, error, isDelError, delError, isSuccess, isDelSuccess, navigate])

    const content = (
        <>
            <Center bg='#45413C' p='0' color='white' axis='both'>
                <Grid w='95vw' maxW='1500' minH='100vh' gap={5} p='5' templateColumns='repeat(12, 1fr)'>
                    <GridItem color='black' colSpan={{ base: "12", sm: "12", md: "12", lg: "12", xl: "12" }}>
                        <HStack color='white'>
                            <Container color='262626' textAlign={['left']} >
                                <Text fontSize={{ base: "24", sm: "24", md: "24", lg: "24", xl: "28" }} fontWeight={600}><FontAwesomeIcon icon={faTicket} /> {T.tickets.EditTicket}</Text><br />
                            </Container>
                            <Container color='262626' textAlign={['right']}>
                                <Button size='sm' onClick={onPageBack} leftIcon={<ArrowBackIcon />} colorScheme='gray' variant='solid' mr='2'>
                                    <Text display={{ base: 'none', sm: 'flex' }} fontSize={{ base: "18", sm: "18", md: "18", lg: "18", xl: "20" }} fontWeight={600}>{T.common.Back}</Text>
                                </Button>
                                <Button
                                    size='sm'
                                    onClick={onSaveTicketClicked}
                                    disabled={!canSave}
                                    leftIcon={<CheckIcon />}
                                    colorScheme={canSave ? "teal" : "gray"}
                                >
                                    <Text display={{ base: 'none', sm: 'flex' }} fontSize={{ base: "18", sm: "18", md: "18", lg: "18", xl: "20" }} fontWeight={600}>{T.common.Save}</Text>
                                </Button>
                            </Container>
                        </HStack>
                        <Box borderRadius='xl' p={2} fontSize={20} fontWeight={400} color='black' border='10px' borderColor='black' bg='white' w="100%">

                            <form onSubmit={e => e.preventDefault()}>
                                <FormControl>

                                    <FormLabel htmlFor='countryTranslate' mt='5' mb='0'>
                                        {T.tickets.TicketName}
                                    </FormLabel>
                                    {formFieldsTicketName.map((form, index) => {
                                        return (
                                            <HStack key={index}>
                                                <Input
                                                    isInvalid={!form.lang}
                                                    name='lang'
                                                    placeholder={T.tickets.IsoCode}
                                                    onChange={event => handleFormChange(event, index)}
                                                    value={form.lang}
                                                    focusBorderColor='blue.400'
                                                    errorBorderColor='red.300'
                                                    minLength="2"
                                                    maxLength="2"
                                                    bg='yellow.100'
                                                    w='10vw'
                                                />
                                                <Input
                                                    isInvalid={!form.ticketName}
                                                    name='ticketName'
                                                    placeholder={T.tickets.TranslatedName}
                                                    onChange={event => handleFormChange(event, index)}
                                                    value={form.ticketName}
                                                    focusBorderColor='blue.400'
                                                    errorBorderColor='red.300'
                                                    bg='yellow.100'
                                                    w='50vw'
                                                />
                                                {index !== 0 &&
                                                    <IconButton
                                                        size='xs'
                                                        name="Remove"
                                                        title={T.common.Delete}
                                                        colorScheme='gray'
                                                        aria-label={T.common.Delete}
                                                        icon={<MinusIcon />}
                                                        onClick={() => removeFields(index)}
                                                    />
                                                }
                                            </HStack>
                                        )
                                    })
                                    }
                                    <IconButton
                                        size='xs'
                                        title={T.common.Add}
                                        onClick={addFields}
                                        colorScheme='gray'
                                        aria-label={T.common.Add}
                                        icon={<AddIcon />}
                                    />

                                    <FormLabel htmlFor='ticketStatus' mt='5' mb='0'>
                                        {T.tickets.Status}
                                    </FormLabel>
                                    <Switch
                                        id='ticketStatus'
                                        name="status"
                                        colorScheme='green'
                                        isChecked={ticketStatus}
                                        onChange={onTicketStatusChanged}
                                    />

                                </FormControl>
                                <br />

                                <HStack>
                                    <Text fontSize={{ base: "10", sm: "10", md: "10", lg: "15", xl: "15" }} fontWeight={600}>{T.common.Created}:</Text>
                                    <Text fontSize={{ base: "10", sm: "10", md: "10", lg: "15", xl: "15" }}>{created}</Text>
                                </HStack>
                                <HStack>
                                    <Text fontSize={{ base: "10", sm: "10", md: "10", lg: "15", xl: "15" }} fontWeight={600}>{T.common.Updated}:</Text>
                                    <Text fontSize={{ base: "10", sm: "10", md: "10", lg: "15", xl: "15" }}>{updated}</Text>
                                </HStack>
                            </form>
                            <br />
                            <br />
                            {deleteButton}

                        </Box>
                    </GridItem>

                </Grid>
            </Center>

        </>
    )

    return content
}

export default EditTicketForm